/**
 * English messages — the canonical message schema.
 * es.ts is typed against `typeof en` so a missing key is a compile error.
 */
export default {
  common: {
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    confirm: "Confirm",
    delete: "Delete",
    deleting: "Deleting…",
    edit: "Edit",
    remove: "Remove",
    removing: "Removing…",
    previous: "Previous",
    next: "Next",
    close: "Close",
    loading: "Loading…",
    unexpectedError: "An unexpected error occurred.",
    you: "You",
  },

  nav: {
    sidebarTitle: "Navigation menu",
    sidebarDescription: "Move between the sections of the app.",
    planning: "Planning",
    team: "Team",
    admin: "Admin",
    account: "Account",
    logout: "Log out",
    language: "Language",
    spanish: "Español",
    english: "English",
  },

  theme: {
    toLight: "Switch to light mode",
    toDark: "Switch to dark mode",
  },

  routeTitles: {
    admin: "Admin",
    account: "Account",
    team: "Team",
    planning: "Planning",
    workoutNew: "New workout",
    workoutDetail: "Workout",
    zenith: "Zenith",
  },

  auth: {
    brandTagline: "Train at your peak",
    login: {
      title: "Welcome back",
      email: "Email",
      password: "Password",
      forgotPassword: "Forgot your password?",
      submit: "Sign in",
      submitting: "Signing in...",
      terms: "By clicking continue, you agree to our {terms} and {privacy}.",
      termsOfService: "Terms of Service",
      privacyPolicy: "Privacy Policy",
      failedTitle: "Login Failed",
    },
    register: {
      title: "Create an account",
      inviteOnly:
        "Zenith is invite-only. After activating your account, a team manager needs to add you to a team.",
      checkEmailTitle: "Check your email",
      checkEmailBody: "We've sent an activation link to {email}.",
      checkEmailFollow:
        "Follow the link in the email to set your password and activate your account.",
      email: "Email",
      submit: "Continue with email",
      submitting: "Sending...",
      hasAccount: "Already have an account?",
      signIn: "Sign in",
      failedTitle: "Registration Failed",
    },
    forgotPassword: {
      title: "Forgot your password?",
      checkEmailTitle: "Check your email",
      checkEmailBody: "If an account exists for {email}, we've sent a reset link.",
      email: "Email",
      submit: "Send reset link",
      submitting: "Sending...",
      remembered: "Remembered your password?",
      signIn: "Sign in",
      failedTitle: "Request Failed",
    },
    setPassword: {
      invalidTitle: "Link invalid or expired",
      invalidBody: "This link is invalid or has expired.",
      requestNewLink: "Request a new link",
      welcomeTitle: "Welcome to Zenith",
      setTitle: "Set your password",
      welcomeDescription: "Set your password to activate your account.",
      newPassword: "New password",
      confirmPassword: "Confirm password",
      passwordTooShort: "Password must be at least 8 characters.",
      passwordsDontMatch: "Passwords do not match.",
      submit: "Set password",
      submitting: "Setting password...",
      successToast: "Password set successfully. You can now sign in.",
      failedTitle: "Failed to set password",
      failedFallback: "This link may have expired. Please request a new one.",
    },
  },

  account: {
    title: "Account",
    loadingProfile: "Loading profile…",
    profileTitle: "Profile",
    profileDescription: "Update your display name. Your email address cannot be changed here.",
    email: "Email",
    firstName: "First name",
    lastName: "Last name",
    save: "Save changes",
    saving: "Saving...",
    updatedToast: "Profile updated.",
    failedTitle: "Update Failed",
  },

  admin: {
    title: "Admin",
    subtitle: "Platform-wide team management",
    createTeamTitle: "Create team",
    createTeamDescription:
      "Only platform admins can create teams. The manager is invited by email.",
    teamName: "Team name",
    teamNamePlaceholder: "North Athletics Club",
    managerEmailPlaceholder: "manager{'@'}club.com",
    managerEmailHint: "An existing user is reused; a new one gets an activation email.",
    lastInvited: "Last team created: {team} — invitation sent to {email}.",
    errors: {
      nameRequired: "Enter the team name.",
      emailRequired: "Enter the manager's email.",
      emailInvalid: "Enter a valid email, for example name{'@'}club.com.",
    },
    managerEmail: "Manager email",
    demo: "Set up as a demo team",
    demoHint:
      "Adds two weeks of example workouts and makes the manager a coach so they can see them.",
    createSubmit: "Create team",
    creating: "Creating…",
    createdToast: "Team created — invitation sent to {email}",
    createFailedTitle: "Create Failed",
    teamsTitle: "Teams",
    teamsDescription: "Every team on the platform.",
    loadFailed: "Failed to load teams.",
    empty: "No teams yet — create one above.",
    memberCount: "{n} member | {n} members",
  },

  team: {
    managementSubtitle: "Team management",
    accessError: "You don't have access to manage this team, or the team could not be loaded.",
    managerRequiredTitle: "Manager access required",
    managerRequiredBody: "You don't manage this team.",
    membersTitle: "Members",
    inviteMember: "Invite member",
    inviteEmail: "Email address",
    roles: "Roles",
    role: {
      manager: "Manager",
      coach: "Coach",
      athlete: "Athlete",
    },
    sendInvite: "Send invite",
    inviting: "Inviting…",
    inviteSentToast: "Invitation sent.",
    inviteFailedTitle: "Invite failed",
    searchPlaceholder: "Search by name or email…",
    searchLabel: "Search members",
    clearSearch: "Clear search",
    filterAll: "All",
    memberCount: "{n} member | {n} members",
    loadMembersFailed: "Failed to load members.",
    noRoles: "No roles",
    removedToast: "{name} removed from the team.",
    removeFailedTitle: "Remove failed",
    rolesButton: "Roles",
    rolesUpdatedToast: "Roles updated for {name}.",
    rolesUpdateFailedTitle: "Update failed",
    invitePlaceholder: "name{'@'}example.com",
    roleHelp: {
      manager: "Administers the team. No access to planning.",
      coach: "Plans workouts and manages groups.",
      athlete: "Sees the whole team schedule.",
    },
    noRolesHelp: "Can't see the schedule until given a role.",
    editRolesFor: "Edit roles for {name}",
    onlyManagerHint: "You're the only manager. Add another first.",
    demoteTitle: "Remove your own Manager role?",
    demoteBody: "You'll lose access to this page. Another manager would have to restore it.",
    removeMemberFor: "Remove {name}",
    removeConfirmTitle: "Remove {name} from the team?",
    removeConfirmBody: "They'll lose access to the schedule.",
    inviteRolesRequired: "Select at least one role.",
    sectionContinued: "continued",
    retry: "Retry",
    pagination: "Pages",
    emptyNoFilter: "No members yet — invite someone to get started.",
    emptyFiltered: "No members match your search or filter.",
    prev: "Previous",
    next: "Next",
    pageOf: "Page {page} of {total}",
  },

  teamSwitcher: {
    teamsLabel: "Teams",
    noTeam: "No team",
  },

  noTeam: {
    title: "No team yet",
    description:
      "You're not a member of any team. Ask a team manager to invite you, then sign back in.",
    shareEmail: "Give this email to your team manager: {email}",
    editProfile: "Edit profile",
    logout: "Log out",
  },

  selectTeam: {
    title: "Select a team",
    description: "Choose which team to work in.",
    loading: "Loading teams…",
  },

  groupManager: {
    title: "Groups",
    description: "Organize the team's workouts into groups, for example by category or level.",
    save: "Save",
    cancelRename: "Cancel rename",
    createdToast: "Group created.",
    createFailedTitle: "Create failed",
    renamedToast: "Group renamed.",
    renameFailedTitle: "Rename failed",
    removedToast: "Group removed.",
    removeFailedTitle: "Remove failed",
    removeConfirm: "Delete this group? It must be empty.",
    onlyGroupHint: "Only group — teams need at least one",
    rename: "Rename",
    removeDisabledTitle: "A team must have at least one group",
    removeDisabledLabel: "Remove group — disabled, a team needs at least one group",
    removeLabel: "Remove group",
    remove: "Remove",
    createLabel: "Create group",
    createPlaceholder: "Group name",
    addGroup: "Add group",
    creating: "Creating…",
  },

  // Week view (group lanes) — owned by the WeekView rebuild.
  weekView: {
    groupColumnLabel: "Group",
    sessionCount: "{n} session | {n} sessions",
    noGroup: "No group",
    add: "Add",
    emptyWeek: "No sessions this week.",
    emptyLane: "No sessions this week for {group}. Try another group.",
    noGroupsMessage: 'This team has no groups yet. Coaches can add one from "Manage groups".',
  },

  // Day view (day rail + day detail) — owned by the DayView build.
  dayView: {
    share: "Share",
    sharePreparing: "Preparing…",
    shareCopied: "Workout copied as text",
    shareDownloaded: "Downloaded",
    shareFailed: "Couldn't share the workout",
    today: "today",
    restDayForGroup: "Rest day for {group}.",
  },

  planning: {
    loadError: "Couldn't load the schedule.",
    retry: "Try again",
    dayLabel: "Day",
    viewSwitcher: "View",
    monthLabel: "Month",
    weekLabel: "Week",
    dayPrev: "Previous week",
    dayNext: "Next week",
    weekPrev: "Previous week",
    weekNext: "Next week",
    monthPrev: "Previous month",
    monthNext: "Next month",
    previousWeek: "Previous week (← / j)",
    nextWeek: "Next week (→ / k)",
    today: "Today (t)",
    todayShort: "Today",
    allGroups: "All groups",
    manageGroups: "Manage groups",
    copyWeek: "Copy this week to…",
    copyWeekTitle: "Copy this week",
    copyWeekDescription:
      "Copy the workouts of the week of {from} to another week. Pick any day of the target week.",
    copyWeekInto: "Copies into the week of {to}. Anything already planned there stays.",
    copyWeekSameWeek: "Pick a different week than the one you are copying from.",
    copyWeekTarget: "Week to copy into",
    copyWeekAction: "Copy week",
    copyWeekDone: "Copied {count} workouts",
    copyWeekEmpty: "There were no workouts to copy",
    copyWeekFailed: "Could not copy the week",
    newWorkout: "New workout (n)",
    newWorkoutShort: "New workout",
    restDay: "Rest day",
    emptyWeekTitle: "No workouts scheduled this week",
    emptyWeekBody: "Start building your training week — add the first workout to any day.",
    emptyWeekBodyMobile: "Start building your week by adding the first workout.",
    emptyMonthTitle: "No workouts scheduled this month",
    emptyMonthBody: "Click any day to open it and start planning.",
    emptyMonthAgenda: "No workouts scheduled this month.",
    addWorkout: "Add a workout",
    newWorkoutOn: "New workout on {date}",
    addWorkoutOnDay: "Add workout on {day}",
    addWorkoutOnDayDate: "Add workout on {day} {date}",
    jumpToWeek: "Open {label}",
    jumpToWeekAria: "Open {label}",
    weekTotal: "Week",
    shortcuts: "Keyboard shortcuts",
    more: "More options",
    moreWorkouts: "{n} more workouts on {label}",
    moreWorkoutsShort: "+{n} more",
    stepCount: "{n} step | {n} steps",
  },

  workoutEditor: {
    backToPlanning: "Back to planning",
    more: "More",
    edit: "Edit",
    duplicate: "Duplicate",
    duplicateOf: "Copy of {title}",
    confirmDelete: "Confirm delete",
    notFound: "Workout not found or you don't have access.",
    addTitle: "Add title",
    titleAriaLabel: "Workout title",
    disciplineAriaLabel: "Discipline",
    disciplinePlaceholder: "Choose discipline",
    disciplineRequired: "Choose a discipline.",
    dateAriaLabel: "Date",
    groupAriaLabel: "Group",
    groupPlaceholder: "Select a group…",
    groupRequired: "Select a group.",
    notes: "Notes",
    addNotes: "Add notes",
    discard: "Discard",
    saveChanges: "Save changes",
    leaveConfirm: "You have unsaved changes. Leave without saving?",
    unsaved: "Unsaved changes",
    disciplineLabel: "Discipline",
    dateLabel: "Date",
    groupLabel: "Group",
    createWorkout: "Create workout",
    saving: "Saving…",
    updatedToast: "Workout updated.",
    createdToast: "Workout created.",
    updateFailedTitle: "Update failed",
    createFailedTitle: "Create failed",
    deletedToast: "Workout deleted.",
    deleteFailedTitle: "Delete failed",
  },

  planText: {
    label: "Session",
    checking: "Checking the text…",
    howTitle: "How to write it (one step per line)",
    help: {
      time: "Time: minutes ('), seconds (\"), hours (h)",
      distance: "Distance: metres, kilometres, miles",
      type: "Step type (with none, it is a main step)",
      rpe: "RPE from 1 to 10",
      repeat:
        "Repeat: N x ( steps separated by / or by lines ). Without parentheses it repeats the rest of the line",
      note: "Any loose word or // text becomes a note",
    },
    typeWords: "warm  rec  rest  cool",
    errorCount: "No errors | 1 error | {n} errors",
    shortcuts:
      "{mod}+D duplicates the line or repeat · {alt}+↑/↓ moves it · {mod}+Z undoes · {mod}+S saves.",
    line: "Line {n}",
    fixToSave: "Fix the text errors to save.",
    tab: "Text",
    stale: "Showing the last valid steps. Fix the text to update them.",
    viewLabel: "Steps or text",
    error: {
      unknownToken:
        "“{text}” isn't recognised. Use time (15'), distance (400m), a type (warm, rec, rest, cool) or {'@'}RPE7.",
      duplicateVolume: "“{text}”: this step already has a volume.",
      duplicateType: "“{text}”: this step already has a type.",
      duplicateRpe: "“{text}”: this step already has an RPE.",
      badVolume: "“{text}”: volume must be above 0 and reasonable (max 24 h or 1000 km).",
      badRpe: "“{text}”: RPE is a single number from 1 to 10 (e.g. {'@'}RPE7).",
      badReps: "“{text}”: repeats run 1 to 999.",
      emptyGroup: "The repeat has no steps: write N x ( … ) or N x step.",
      unclosed: "Missing closing parenthesis.",
      unexpectedClose: "Extra closing parenthesis.",
      groupTrailing:
        "“{text}”: a repeat can't have its own RPE, type or note. Put it on each step.",
    },
  },
  planEditor: {
    empty: "No steps yet.",
    heading: "Steps",
    guide: "Build the session step by step. Tap a value (time, distance, RPE) to change it.",
    guideTouch: "Build the session step by step. Tap a step to edit it.",
    addStep: "Add step",
    addRepeat: "Add repeat",
    addStepToRepeatN: "Add a step to the {n}× repeat",
    addRepeatToRepeat: "Add a repeat inside",
    kindMeters: "Distance · m",
    kindKm: "Distance · km",
    kindMiles: "Distance · mi",
    addStepToRepeat: "Add step to repeat",
    addStepBelow: "Add step below",
    phase: {
      warmup: "Warm-up",
      main: "Main set",
      cooldown: "Cool-down",
    },
    workTypeLabel: "Work type",
    volumeLabel: "Step volume",
    volumeKindLabel: "Volume type",
    timeMode: "Time",
    hoursLabel: "Hours",
    minutesLabel: "Minutes",
    secondsLabel: "Seconds",
    distanceValueLabel: "Distance",
    repsLabel: "Repeats",
    rowMenuStep: "Step options",
    rowMenuRepeat: "Repeat options",
    moveUp: "Move up",
    moveDown: "Move down",
    moveIntoPrevRepeat: "Move into repeat above",
    moveIntoNextRepeat: "Move into repeat below",
    moveOutOfRepeat: "Take out of repeat",
    repeatThis: "Repeat",
    unwrapRepeat: "Undo repeat",
    removeStep: "Delete",
    removeRepeat: "Delete repeat",
    duplicate: "Duplicate",
    stepRemoved: "Step deleted",
    repeatRemoved: "Repeat deleted",
    undo: "Undo",
    notesPlaceholder: "Add note",
    notesLabel: "Step notes",
    noRpe: "No RPE",
    editStep: "Edit step",
    editStepDescription: "Change this step's type, volume, RPE or note.",
    done: "Done",
    editRepeat: "Edit repeat",
    editRepeatDescription: "Change how many times this block repeats.",
    repsDecrease: "One less repeat",
    repsIncrease: "One more repeat",
    shortcutsHint: "⌥↑ ⌥↓ reorder a step · Enter in a value moves to the next field · ⌘Z undoes.",
  },

  workoutProfile: {
    ariaLabel: "Workout profile, {n} steps. Bar width is its share of the workout, height is RPE.",
    noRpe: "no RPE",
  },

  intensityInput: {
    groupLabel: "RPE intensity (1–10)",
    rpeValue: "RPE {n}",
  },

  workType: {
    warmUp: "Warm-up",
    main: "Main",
    recover: "Recover",
    rest: "Rest",
    coolDown: "Cool-down",
    other: "Other",
    work: "Work",
    repeat: "repeat",
    repeats: "repeats",
    emptyGroup: "Empty group",
    unknownStep: "(unknown step)",
  },

  workoutType: {
    run: "Run",
    bike: "Bike",
    swim: "Swim",
    strength: "Strength",
    other: "Other",
    workout: "Workout",
  },

  errors: {
    generic: "Something went wrong. Please try again.",
    INVALID_CREDENTIALS: "Incorrect email or password.",
    USER_EMAIL_ALREADY_EXISTS: "That email address is already registered.",
    INVALID_RESET_TOKEN: "This link is invalid or has expired.",
    INVALID_PASSWORD: "Password must be at least 8 characters long.",
    FORBIDDEN: "You don't have permission to do that.",
    MEMBER_ALREADY_IN_TEAM: "That person is already a member of this team.",
    TEAM_ALREADY_EXISTS: "A team with that name already exists.",
    EMPTY_TEAM_NAME: "Team name cannot be empty.",
    EMPTY_GROUP_NAME: "Group name cannot be empty.",
    GROUP_HAS_WORKOUTS:
      "This group still has workouts. Move them to another group or delete them first.",
    LAST_GROUP: "A team needs at least one group.",
    GROUP_ALREADY_EXISTS: "A group with that name already exists.",
    PLATFORM_ADMIN_FORBIDDEN: "You don't have permission to perform platform admin actions.",
    INVALID_LOCALE: "Language must be Spanish or English.",
    INVALID_WORKOUT_PLAN: "The workout plan is invalid.",
    MISSING_STEPS: "At least one step is required.",
    MISSING_REPS: "Number of repeats is required.",
    INVALID_MEASURE: "Invalid measurement.",
    INVALID_MEASURE_UNIT: "Unknown measurement unit.",
    INVALID_MEASURE_VALUE: "Invalid measurement value.",
    UNKNOWN_WORKOUT_TYPE: "Unknown workout type.",
    UNKNOWN_WORK_TYPE: "Unknown work type.",
  },
};
